import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge from '../components/UI/Badge';
import { brokerAPI, dataProviderAPI, notificationsAPI } from '../services/api';
import { PageTransition, Spinner } from '@/components/motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/UI/dialog';
import { usePushNotifications } from '../hooks/usePushNotifications';

// ─── Data ────────────────────────────────────────────────────────────────────

const brokerPresets = [
  { value: 'alpaca',        label: 'Alpaca',                type: 'stocks' },
  { value: 'ibkr',          label: 'Interactive Brokers',   type: 'stocks' },
  { value: 'schwab',        label: 'Charles Schwab',         type: 'stocks' },
  { value: 'binance',       label: 'Binance',               type: 'crypto' },
  { value: 'coinbase',      label: 'Coinbase',              type: 'crypto' },
  { value: 'kraken',        label: 'Kraken',                type: 'crypto' },
  { value: 'custom',        label: 'Custom',                type: 'both'   },
];

const themes = [
  { value: 'dark',   label: 'Dark',   icon: '🌙', description: 'Dark interface',       available: true  },
  { value: 'light',  label: 'Light',  icon: '☀️',  description: 'Light interface',      available: false },
  { value: 'system', label: 'System', icon: '💻', description: 'Follow OS preference', available: false },
];

function readStorage(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; }
  catch { return fallback; }
}

// ─── General ─────────────────────────────────────────────────────────────────

function GeneralSettings() {
  const [settings, setSettings] = useState(() =>
    readStorage('proton_settings', { theme: 'dark' })
  );
  const [saved, setSaved] = useState(false);

  function update(patch) { setSettings((s) => ({ ...s, ...patch })); }

  function save() {
    localStorage.setItem('proton_settings', JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="space-y-8">
      {/* Theme */}
      <Card title="Theme">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {themes.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => t.available && update({ theme: t.value })}
              disabled={!t.available}
              className={`relative flex flex-col items-center gap-2 p-5 rounded-xl border-2 transition-all text-center ${
                settings.theme === t.value
                  ? 'border-primary bg-primary/10'
                  : t.available
                  ? 'border-border bg-accent/10 hover:border-muted-foreground cursor-pointer'
                  : 'border-border bg-card/30 opacity-40 cursor-not-allowed'
              }`}
            >
              {settings.theme === t.value && (
                <span className="absolute top-2 left-2 text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded font-medium">
                  Active
                </span>
              )}
              {!t.available && (
                <span className="absolute top-2 right-2 text-xs bg-card text-muted-foreground px-1.5 py-0.5 rounded">
                  Soon
                </span>
              )}
              <span className="text-3xl mt-1">{t.icon}</span>
              <span className="text-sm font-semibold text-foreground">{t.label}</span>
              <span className="text-xs text-muted-foreground">{t.description}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Push Notifications */}
      <PushNotificationCard />

      {/* Notification Preferences */}
      <NotificationPreferencesCard />

      <div className="flex items-center gap-4">
        <Button onClick={save}>{saved ? '✓ Saved' : 'Save Changes'}</Button>
        {saved && <span className="text-sm text-green-400">Settings saved successfully</span>}
      </div>
    </div>
  );
}

function PushNotificationCard() {
  const { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe } = usePushNotifications();

  async function handleToggle() {
    if (isSubscribed) {
      const ok = await unsubscribe();
      if (ok) toast.success('Push notifications disabled');
      else toast.error('Failed to disable push notifications');
    } else {
      const ok = await subscribe();
      if (ok) toast.success('Push notifications enabled');
      else if (permission === 'denied') toast.error('Notification permission denied. Please allow in browser settings.');
      else toast.error('Failed to enable push notifications');
    }
  }

  const statusText = !isSupported
    ? 'Not supported in this browser'
    : permission === 'denied'
    ? 'Blocked by browser — update in browser settings'
    : isSubscribed
    ? 'Enabled'
    : 'Disabled';

  const statusColor = !isSupported || permission === 'denied'
    ? 'text-red-400'
    : isSubscribed
    ? 'text-green-400'
    : 'text-muted-foreground';

  return (
    <Card title="Push Notifications">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-foreground">Desktop & mobile push notifications</p>
          <p className={`text-xs mt-1 ${statusColor}`}>{statusText}</p>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          disabled={!isSupported || permission === 'denied' || loading}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            isSubscribed ? 'bg-primary' : 'bg-muted-foreground/30'
          } ${(!isSupported || permission === 'denied' || loading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isSubscribed ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </Card>
  );
}

const NOTIFICATION_EVENT_LABELS = {
  order_submitted: { label: 'Order Submitted', desc: 'When an order is sent to the broker' },
  order_filled: { label: 'Order Filled', desc: 'When an order is filled (full or partial)' },
  order_rejected: { label: 'Order Rejected', desc: 'When an order is rejected by the broker' },
  order_canceled: { label: 'Order Canceled', desc: 'When an order is canceled' },
  position_opened: { label: 'Position Opened', desc: 'When a new position is created from a fill' },
  position_closed: { label: 'Position Closed', desc: 'When a position is fully closed' },
  stop_loss_triggered: { label: 'Stop Loss Triggered', desc: 'When a stop loss exit is executed' },
  strategy_stopped: { label: 'Strategy Stopped', desc: 'When a strategy is auto-stopped' },
  strategy_error: { label: 'Strategy Error', desc: 'When a strategy encounters an error' },
};

function NotificationPreferencesCard() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    notificationsAPI.getPreferences()
      .then((res) => setPrefs(res.data || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleToggle(key) {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSaving(true);
    try {
      await notificationsAPI.updatePreferences(updated);
    } catch (err) {
      toast.error(`Failed to save: ${err.message}`);
      setPrefs(prefs); // revert
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Card title="Notification Preferences"><div className="py-6 text-center text-muted-foreground text-sm">Loading...</div></Card>;

  return (
    <Card title="Notification Preferences" subtitle="Global defaults — can be overridden per strategy">
      <div className="space-y-1">
        {Object.entries(NOTIFICATION_EVENT_LABELS).map(([key, { label, desc }]) => (
          <div key={key} className="flex items-center justify-between py-2 px-1 rounded hover:bg-accent/5">
            <div>
              <p className="text-sm text-foreground">{label}</p>
              <p className="text-xs text-muted-foreground/60">{desc}</p>
            </div>
            <button
              type="button"
              onClick={() => handleToggle(key)}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                prefs?.[key] ? 'bg-primary' : 'bg-muted-foreground/30'
              } ${saving ? 'opacity-50' : 'cursor-pointer'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                prefs?.[key] ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Broker Validation ────────────────────────────────────────────────────────

const ALL_VALIDATION_TESTS = [
  { name: 'account',              label: 'Account Access',         category: 'common' },
  { name: 'positions',            label: 'Positions',              category: 'common' },
  { name: 'orders',               label: 'Orders',                 category: 'common' },
  { name: 'historicalData',       label: 'Stock Market Data',      category: 'stocks' },
  { name: 'quote',                label: 'Stock Quote',            category: 'stocks' },
  { name: 'cryptoMarketData',     label: 'Crypto Market Data',     category: 'crypto' },
  { name: 'orderPlacement',       label: 'Stock Order Placement',  category: 'stocks' },
  { name: 'cryptoOrderPlacement', label: 'Crypto Order Placement', category: 'crypto' },
];

function getValidationTests(brokerType = 'both') {
  return ALL_VALIDATION_TESTS.filter((t) => {
    if (t.category === 'common') return true;
    if (brokerType === 'both') return true;
    return t.category === brokerType;
  });
}

function ValidationRow({ test, result }) {
  const status = result?.status || 'idle';
  const isSkippedPass = status === 'passed' && result?.detail?.startsWith('Skipped');

  const icon = isSkippedPass
    ? <span className="text-red-400/60 text-sm">&#10005;</span>
    : {
        idle:    <span className="w-2 h-2 rounded-full bg-muted-foreground/40 inline-block" />,
        running: <Spinner className="h-3.5 w-3.5" />,
        passed:  <span className="text-green-400 text-sm">&#10003;</span>,
        failed:  <span className="text-red-400 text-sm">&#10005;</span>,
        skipped: <span className="text-muted-foreground/50 text-sm">&#8212;</span>,
      }[status];

  const detail = isSkippedPass
    ? <span className="text-red-300/60 text-xs">{result.detail}</span>
    : {
        idle:    null,
        running: <span className="text-muted-foreground text-xs">Running...</span>,
        passed:  <span className="text-green-300 text-xs">{result?.detail}</span>,
        failed:  <span className="text-red-300 text-xs">{result?.error}</span>,
        skipped: <span className="text-muted-foreground/50 text-xs">Skipped</span>,
      }[status];

  return (
    <div className={`flex items-center gap-3 py-1.5 ${isSkippedPass ? 'bg-red-900/10 rounded-md px-1 -mx-1' : ''}`}>
      <div className="w-5 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <span className={`text-sm ${status === 'skipped' ? 'text-muted-foreground/50' : 'text-foreground'}`}>
          {test.label}
        </span>
        {detail && <div className="mt-0.5">{detail}</div>}
      </div>
    </div>
  );
}

function BrokerValidationPanel({ credentials }) {
  const [results, setResults] = useState({});
  const [summary, setSummary] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const visibleTests = getValidationTests(credentials?.type);

  useEffect(() => {
    if (!credentials) return;

    setResults({});
    setSummary(null);
    setCollapsed(false);

    const controller = new AbortController();
    const apiUrl = import.meta.env.VITE_API_URL || '';

    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/broker-connections/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(credentials),
          signal: controller.signal,
        });

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete line in buffer

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.done) {
                setSummary({ passed: event.passed, failed: event.failed, total: event.total });
              } else if (event.test) {
                setResults((prev) => ({ ...prev, [event.test]: event }));
              }
            } catch { /* ignore malformed lines */ }
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setSummary({ error: err.message });
        }
      }
    })();

    return () => controller.abort();
  }, [credentials]);

  // Count skipped passes (returned "Skipped" detail) separately from real passes
  const skippedCount = Object.values(results).filter(
    (r) => r.status === 'passed' && r.detail?.startsWith('Skipped')
  ).length;
  const realPassed = summary ? summary.passed - skippedCount : 0;
  const allPassed = summary && summary.failed === 0 && skippedCount === 0 && !summary.error;
  const hasSkips = summary && skippedCount > 0 && summary.failed === 0;
  const summaryColor = summary?.error ? 'text-red-400' : allPassed ? 'text-green-400' : 'text-yellow-400';
  const summaryText = summary
    ? (summary.error
      ? `Validation error: ${summary.error}`
      : hasSkips
        ? `${realPassed}/${summary.total} passed · ${skippedCount} skipped`
        : `${summary.passed}/${summary.total} tests passed`)
    : null;

  return (
    <div className="mt-3 bg-accent/10 border border-border rounded-lg">
      {/* Clickable header — always visible */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
      >
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Validation Tests</span>
        <div className="flex items-center gap-2">
          {summaryText && <span className={`text-xs font-medium ${summaryColor}`}>{summaryText}</span>}
          <svg className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${collapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Collapsible body */}
      {!collapsed && (
        <div className="px-3 pb-3 space-y-0.5">
          {visibleTests.map((test) => (
            <ValidationRow key={test.name} test={test} result={results[test.name]} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Broker Modal ─────────────────────────────────────────────────────────────

const EMPTY_FORM = { preset: '', name: '', type: 'stocks', environment: 'paper', apiKey: '', apiSecret: '' };

function BrokerModal({ broker, onSave, onClose, autoValidate }) {
  const isEdit = !!broker?.id;
  const isSchwab = (broker?.preset === 'schwab') || false;
  const [form, setForm] = useState(
    broker ? { preset: broker.preset, name: broker.name, type: broker.type, environment: broker.environment, apiKey: broker.api_key ?? broker.apiKey ?? '', apiSecret: '' }
           : { ...EMPTY_FORM }
  );
  const [showSecret, setShowSecret]   = useState(false);
  const [error, setError]             = useState(null);
  const [testStatus, setTestStatus]   = useState(null); // null | 'testing' | 'success' | 'error'
  const [testMessage, setTestMessage] = useState('');
  const [validationCreds, setValidationCreds] = useState(autoValidate || null); // triggers BrokerValidationPanel

  const isSchwabPreset = form.preset === 'schwab';

  function set(patch) {
    setForm((f) => ({ ...f, ...patch }));
    // Reset test result when credentials change
    if ('apiKey' in patch || 'apiSecret' in patch || 'environment' in patch) {
      setTestStatus(null);
      setTestMessage('');
      setValidationCreds(null);
    }
  }

  function handlePreset(value) {
    const p = brokerPresets.find((b) => b.value === value);
    const patch = { preset: value, name: p?.label ?? value, type: p?.type ?? 'stocks' };
    // Schwab: force live + stocks (no paper, no crypto)
    if (value === 'schwab') {
      patch.environment = 'live';
      patch.type = 'stocks';
    }
    set(patch);
    setTestStatus(null);
    setTestMessage('');
  }

  async function handleSchwabAuth() {
    if (!broker?.id) return;
    try {
      const res = await brokerAPI.getSchwabAuthUrl(broker.id);
      if (res.url) {
        window.open(res.url, 'schwab_auth', 'width=600,height=700');
      }
    } catch (err) {
      toast.error(`Failed to start Schwab auth: ${err.message}`);
    }
  }

  async function handleTest() {
    setTestStatus('testing');
    setTestMessage('');
    try {
      const res = await brokerAPI.test({
        preset: form.preset,
        apiKey: form.apiKey,
        apiSecret: form.apiSecret,
        environment: form.environment,
        ...(isSchwabPreset && broker?.id ? { connectionId: broker.id } : {}),
      });
      if (res.success) {
        const acct = res.account;
        const detail = acct
          ? ` · Account ${acct.status} · Equity $${parseFloat(acct.equity).toLocaleString()}`
          : '';
        setTestStatus('success');
        setTestMessage(res.message + detail);
      } else {
        setTestStatus('error');
        setTestMessage(res.message);
      }
    } catch (err) {
      setTestStatus('error');
      setTestMessage(err.message);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (isSchwabPreset) {
      // Schwab uses server-level credentials — no API key/secret from user
      const payload = {
        preset: form.preset,
        name: form.name,
        type: 'stocks',
        environment: 'live',
        apiKey: 'schwab-server-managed',
        apiSecret: 'schwab-server-managed',
      };
      onSave(broker?.id, payload);
      return;
    }
    if (!form.apiKey.trim()) return setError('API Key is required.');
    if (!isEdit && !form.apiSecret.trim()) return setError('API Secret is required.');
    const payload = {
      preset: form.preset,
      name: form.name,
      type: form.type,
      environment: form.environment,
      apiKey: form.apiKey,
      ...(form.apiSecret ? { apiSecret: form.apiSecret } : {}),
    };
    onSave(broker?.id, payload);
  }

  const schwabAuthorized = isSchwabPreset && isEdit && broker?.oauth_configured;
  const canTest = isSchwabPreset
    ? schwabAuthorized
    : (!!form.apiKey.trim() && (!!form.apiSecret.trim() || isEdit));

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Broker' : 'Add Broker'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 px-3 py-2 rounded-lg text-sm">{error}</div>
          )}

          {/* Broker select */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">Broker</label>
            <select
              value={form.preset}
              onChange={(e) => handlePreset(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="" disabled>Select a broker...</option>
              {brokerPresets.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            {form.preset === 'custom' && (
              <input
                type="text"
                placeholder="Custom broker name"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                className="mt-2 w-full px-3 py-2 bg-background border border-border text-foreground rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            )}
          </div>

          {/* Asset type */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">Asset Type</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'stocks', label: 'Stocks' },
                { value: 'crypto', label: 'Crypto' },
                { value: 'both',   label: 'Both'   },
              ].map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => !isSchwabPreset && set({ type: t.value })}
                  disabled={isSchwabPreset && t.value !== 'stocks'}
                  className={`py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    form.type === t.value
                      ? 'border-primary bg-primary/20 text-foreground'
                      : isSchwabPreset && t.value !== 'stocks'
                      ? 'border-border bg-card/50 text-muted-foreground/50 cursor-not-allowed'
                      : 'border-border bg-card text-muted-foreground hover:border-muted-foreground'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {isSchwabPreset && (
              <p className="text-xs text-muted-foreground/60 mt-1">Schwab supports stocks only</p>
            )}
          </div>

          {/* Environment — hidden for Schwab (always live) */}
          {!isSchwabPreset && (
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Environment</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'paper', label: 'Paper', active: 'border-primary bg-primary/20 text-foreground' },
                  { value: 'live',  label: 'Live',  active: 'border-yellow-500 bg-yellow-900/30 text-foreground' },
                ].map((env) => (
                  <button
                    key={env.value}
                    type="button"
                    onClick={() => set({ environment: env.value })}
                    className={`py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                      form.environment === env.value
                        ? env.active
                        : 'border-border bg-card text-muted-foreground hover:border-muted-foreground'
                    }`}
                  >
                    {env.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Schwab — Authorize button shown immediately (saves + authorizes in one step) */}
          {isSchwabPreset && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={async () => {
                  // If not yet saved, save first then authorize
                  if (!broker?.id) {
                    try {
                      const res = await onSave(null, {
                        preset: 'schwab',
                        name: form.name || 'Charles Schwab',
                        type: 'stocks',
                        environment: 'live',
                        apiKey: 'schwab-server-managed',
                        apiSecret: 'schwab-server-managed',
                      });
                      // onSave reloads and reopens — handleSchwabAuth will fire on the reopened modal
                    } catch (err) {
                      return;
                    }
                  } else {
                    handleSchwabAuth();
                  }
                }}
                className={`w-full py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors cursor-pointer ${
                  broker?.oauth_configured
                    ? 'border-green-500/50 bg-green-500/10 text-green-400 hover:bg-green-500/20'
                    : 'border-blue-500 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20'
                }`}
              >
                {broker?.oauth_configured ? 'Re-authorize with Schwab' : 'Authorize with Schwab'}
              </button>
              {broker?.oauth_configured && (
                <p className="text-[10px] text-green-400/60 text-center">
                  Schwab authorized. You can test the connection below.
                </p>
              )}
              {broker?.oauth_refresh_expires_at && (
                <SchwabExpiryWarning expiresAt={broker.oauth_refresh_expires_at} />
              )}
            </div>
          )}

          {/* API Key — hidden for Schwab */}
          {!isSchwabPreset && (
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">API Key</label>
              <input
                type="text"
                value={form.apiKey}
                onChange={(e) => set({ apiKey: e.target.value })}
                placeholder="Enter API key"
                className="w-full px-3 py-2 bg-background border border-border text-foreground rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring font-mono text-sm"
              />
            </div>
          )}

          {/* API Secret — hidden for Schwab */}
          {!isSchwabPreset && (
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                API Secret
                {isEdit && <span className="text-muted-foreground/70 font-normal ml-1">(leave blank to keep existing)</span>}
              </label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={form.apiSecret}
                  onChange={(e) => set({ apiSecret: e.target.value })}
                  placeholder={isEdit ? '••••••••••••••••' : 'Enter API secret'}
                  className="w-full px-3 py-2 pr-10 bg-background border border-border text-foreground rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showSecret ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
          )}

          {/* Test Connection — hidden for Schwab until OAuth is complete */}
          <div>
            {isSchwabPreset && !schwabAuthorized ? (
              <div className="text-center py-2">
                <p className="text-xs text-muted-foreground/50">
                  {isEdit ? 'Authorize with Schwab above to enable testing.' : 'Save the connection first, then authorize with Schwab.'}
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleTest}
                disabled={!canTest || testStatus === 'testing'}
                className={`w-full py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                  !canTest
                    ? 'border-border bg-card/50 text-muted-foreground/50 cursor-not-allowed'
                    : testStatus === 'testing'
                    ? 'border-primary/50 bg-primary/10 text-primary cursor-not-allowed'
                    : 'border-primary bg-primary/5 text-primary hover:bg-primary/20 hover:border-primary/80 cursor-pointer'
                }`}
              >
                {testStatus === 'testing' ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner className="h-3.5 w-3.5" />
                    Testing…
                  </span>
                ) : 'Test Connection'}
              </button>
            )}
            {!canTest && (
              <p className="text-xs text-muted-foreground/70 mt-1.5 text-center">
                {isEdit ? 'Enter your API secret above to test' : 'Fill in API key and secret to test'}
              </p>
            )}
            {testStatus === 'success' && (
              <div className="mt-2 flex items-start gap-2 bg-green-900/30 border border-green-700 text-green-300 px-3 py-2 rounded-lg text-xs">
                <span className="mt-0.5 flex-shrink-0">✓</span>
                <span>{testMessage}</span>
              </div>
            )}
            {testStatus === 'error' && (
              <div className="mt-2 flex items-start gap-2 bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded-lg text-xs">
                <span className="mt-0.5 flex-shrink-0">✕</span>
                <span>{testMessage}</span>
              </div>
            )}

            {/* Full Validation */}
            <button
              type="button"
              onClick={() => setValidationCreds({
                preset: form.preset,
                apiKey: form.apiKey,
                apiSecret: form.apiSecret,
                environment: form.environment,
                type: form.type,
                ...(isSchwabPreset && broker?.id ? { connectionId: broker.id } : {}),
              })}
              disabled={!canTest}
              className={`mt-2 w-full py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                !canTest
                  ? 'border-border bg-card/50 text-muted-foreground/50 cursor-not-allowed'
                  : 'border-border bg-card text-foreground hover:bg-accent/20 hover:border-muted-foreground cursor-pointer'
              }`}
            >
              Run Full Validation
            </button>
            <p className="text-[10px] text-muted-foreground/60 mt-1.5 text-center">
              Minimum $100 account balance required for order placement tests
            </p>
            {validationCreds && <BrokerValidationPanel credentials={validationCreds} />}
          </div>

          {/* Schwab OAuth section moved above — no duplicate needed here */}

          {/* Footer */}
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-1">{isEdit ? 'Update Broker' : 'Add Broker'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Schwab Expiry Warning ───────────────────────────────────────────────────

function SchwabExpiryWarning({ expiresAt }) {
  if (!expiresAt) return null;
  const expires = new Date(expiresAt);
  const daysLeft = Math.max(0, Math.ceil((expires - Date.now()) / (1000 * 60 * 60 * 24)));
  const isUrgent = daysLeft <= 2;
  const isExpired = daysLeft === 0;

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
      isExpired ? 'bg-red-900/30 border border-red-700 text-red-300'
        : isUrgent ? 'bg-yellow-900/30 border border-yellow-700 text-yellow-300'
        : 'bg-accent/20 border border-border text-muted-foreground'
    }`}>
      <span>{isExpired ? 'Re-auth needed — refresh token expired' : `Refresh token expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}</span>
    </div>
  );
}

// ─── Brokers ─────────────────────────────────────────────────────────────────

const TYPE_LABEL   = { stocks: 'Stocks', crypto: 'Crypto', both: 'Stocks + Crypto' };
const TYPE_VARIANT = { stocks: 'info', crypto: 'warning', both: 'success' };

function BrokersSettings() {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null); // null | 'add' | <broker row>
  const [autoValidate, setAutoValidate] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await brokerAPI.getAll();
      setBrokers(res.data ?? []);
    } catch (err) {
      toast.error(`Failed to load brokers: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle Schwab OAuth callback — both postMessage (from popup) and URL params (fallback)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'SCHWAB_AUTH_SUCCESS') {
        toast.success('Schwab authorization successful');
        load(); // reload broker list to update oauth_configured flag
        // Reopen the modal for the authorized connection
        const connId = event.data.connectionId;
        if (connId) {
          brokerAPI.getAll().then((res) => {
            const updated = (res.data || []).find(b => b.id === connId);
            if (updated) setModal(updated);
          });
        }
      } else if (event.data?.type === 'SCHWAB_AUTH_ERROR') {
        toast.error(`Schwab authorization failed: ${decodeURIComponent(event.data.reason || 'Unknown error')}`);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [load]);

  // Fallback: handle URL params if popup didn't close properly
  useEffect(() => {
    const schwabAuth = searchParams.get('schwab_auth');
    if (schwabAuth === 'success') {
      toast.success('Schwab authorization successful');
      searchParams.delete('schwab_auth');
      setSearchParams(searchParams, { replace: true });
      load();
    } else if (schwabAuth === 'error') {
      const reason = searchParams.get('reason') || 'Unknown error';
      toast.error(`Schwab authorization failed: ${reason}`);
      searchParams.delete('schwab_auth');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, load]);

  async function handleSave(id, payload) {
    try {
      if (id) {
        await brokerAPI.update(id, payload);
        toast.success('Broker updated');
        setModal(null);
      } else {
        const res = await brokerAPI.create(payload);
        toast.success('Broker added');
        if (payload.preset === 'schwab' && res.data?.id) {
          // For Schwab, reload and reopen so Authorize button can trigger OAuth
          setModal(null);
          const updatedBrokers = await brokerAPI.getAll();
          setBrokers(updatedBrokers.data || []);
          const newConn = (updatedBrokers.data || []).find(b => b.id === res.data.id);
          if (newConn) {
            setModal(newConn);
            // Auto-trigger OAuth authorization
            try {
              const authRes = await brokerAPI.getSchwabAuthUrl(newConn.id);
              if (authRes.url) {
                window.open(authRes.url, 'schwab_auth', 'width=600,height=700');
              }
            } catch (authErr) {
              toast.error(`Failed to start Schwab auth: ${authErr.message}`);
            }
            return;
          }
        } else {
          setModal(null);
        }
      }
      setAutoValidate(null);
      load();
    } catch (err) {
      toast.error(`Failed to save broker: ${err.message}`);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this broker? This cannot be undone.')) return;
    try {
      await brokerAPI.remove(id);
      load();
      toast.success('Broker removed');
    } catch (err) {
      toast.error(`Failed to remove broker: ${err.message}`);
    }
  }

  async function handleToggle(id) {
    try {
      await brokerAPI.toggleStatus(id);
      load();
    } catch (err) {
      toast.error(`Failed to toggle broker: ${err.message}`);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={() => setModal('add')}>+ Add Broker</Button>
      </div>

      {brokers.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <div className="text-5xl mb-4">🏦</div>
            <p className="text-muted-foreground mb-1">No brokers connected</p>
            <p className="text-sm text-muted-foreground/70 mb-6">Connect a broker to start live or paper trading.</p>
            <Button onClick={() => setModal('add')}>+ Add Broker</Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {brokers.map((broker) => (
            <BrokerRow
              key={broker.id}
              broker={broker}
              onEdit={() => setModal(broker)}
              onToggle={() => handleToggle(broker.id)}
              onDelete={() => handleDelete(broker.id)}
            />
          ))}
        </div>
      )}

      {modal && (
        <BrokerModal
          broker={modal === 'add' ? null : modal}
          onSave={handleSave}
          onClose={() => { setModal(null); setAutoValidate(null); }}
          autoValidate={autoValidate}
        />
      )}
    </div>
  );
}

function BrokerRow({ broker, onEdit, onToggle, onDelete }) {
  const key = broker.api_key ?? broker.apiKey ?? '';
  const masked = key ? key.slice(0, 6) + '•'.repeat(Math.max(4, key.length - 6)) : '—';

  // Schwab re-auth warning
  const schwabExpiring = broker.preset === 'schwab' && broker.oauth_refresh_expires_at &&
    (new Date(broker.oauth_refresh_expires_at) - Date.now()) < 2 * 24 * 60 * 60 * 1000;
  const schwabExpired = broker.preset === 'schwab' && broker.oauth_refresh_expires_at &&
    new Date(broker.oauth_refresh_expires_at) <= new Date();

  return (
    <div className="bg-card border border-border rounded-xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      {/* Left: avatar + info */}
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center text-foreground font-bold text-sm flex-shrink-0 select-none">
          {broker.name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="text-sm font-semibold text-foreground">{broker.name}</p>
            <Badge variant={broker.status === 'active' ? 'success' : 'neutral'}>
              {broker.status.toUpperCase()}
            </Badge>
            <Badge variant={TYPE_VARIANT[broker.type] ?? 'neutral'}>
              {TYPE_LABEL[broker.type] ?? broker.type}
            </Badge>
            <Badge variant={broker.environment === 'live' ? 'warning' : 'info'}>
              {broker.environment === 'live' ? 'LIVE' : 'PAPER'}
            </Badge>
            {schwabExpired && (
              <Badge variant="danger">RE-AUTH NEEDED</Badge>
            )}
            {schwabExpiring && !schwabExpired && (
              <Badge variant="warning">AUTH EXPIRING</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground/70 font-mono">{masked}</p>
        </div>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
        <ActionBtn onClick={onEdit} className="text-muted-foreground bg-card hover:bg-accent/10">
          Edit
        </ActionBtn>
        <ActionBtn
          onClick={onToggle}
          className={broker.status === 'active'
            ? 'text-yellow-400 bg-yellow-900/30 hover:bg-yellow-900/50'
            : 'text-green-400 bg-green-900/30 hover:bg-green-900/50'
          }
        >
          {broker.status === 'active' ? 'Suspend' : 'Activate'}
        </ActionBtn>
        <ActionBtn onClick={onDelete} className="text-red-400 bg-red-900/30 hover:bg-red-900/50">
          Delete
        </ActionBtn>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, className, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${className}`}
    >
      {children}
    </button>
  );
}

// ─── Market Data Providers ────────────────────────────────────────────────────

const dataPresets = [
  { value: 'yahoo',          label: 'Yahoo Finance',   type: 'both',   free: true  },
  { value: 'alpaca',         label: 'Alpaca',          type: 'both',   free: false },
  { value: 'alphavantage',   label: 'Alpha Vantage',   type: 'stocks', free: false },
  { value: 'polygon',        label: 'Polygon.io',      type: 'stocks', free: false },
  { value: 'coingecko',      label: 'CoinGecko',       type: 'crypto', free: true  },
  { value: 'binance',        label: 'Binance',         type: 'crypto', free: false },
  { value: 'coinbase',       label: 'Coinbase',        type: 'crypto', free: false },
  { value: 'cryptocompare',  label: 'CryptoCompare',   type: 'crypto', free: false },
];

const DATA_VALIDATION_TESTS = [
  { name: 'account',          label: 'Account Access',        category: 'common' },
  { name: 'stockQuote',       label: 'Stock Quote',           category: 'stocks' },
  { name: 'stockHistorical',  label: 'Stock Historical Data', category: 'stocks' },
  { name: 'cryptoQuote',      label: 'Crypto Quote',          category: 'crypto' },
  { name: 'cryptoHistorical', label: 'Crypto Historical Data',category: 'crypto' },
];

function getDataValidationTests(dataType = 'both') {
  return DATA_VALIDATION_TESTS.filter((t) => {
    if (t.category === 'common') return true;
    if (dataType === 'both') return true;
    return t.category === dataType;
  });
}

function DataValidationPanel({ credentials }) {
  const [results, setResults] = useState({});
  const [summary, setSummary] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const visibleTests = getDataValidationTests(credentials?.type);

  useEffect(() => {
    if (!credentials) return;

    setResults({});
    setSummary(null);
    setCollapsed(false);

    const controller = new AbortController();
    const apiUrl = import.meta.env.VITE_API_URL || '';

    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/market-data-providers/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(credentials),
          signal: controller.signal,
        });

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.done) {
                setSummary({ passed: event.passed, failed: event.failed, total: event.total });
              } else if (event.test) {
                setResults((prev) => ({ ...prev, [event.test]: event }));
              }
            } catch { /* ignore malformed lines */ }
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setSummary({ error: err.message });
        }
      }
    })();

    return () => controller.abort();
  }, [credentials]);

  const skippedCount = Object.values(results).filter(
    (r) => r.status === 'passed' && r.detail?.startsWith('N/A')
  ).length;
  const realPassed = summary ? summary.passed - skippedCount : 0;
  const allPassed = summary && summary.failed === 0 && skippedCount === 0 && !summary.error;
  const hasSkips = summary && skippedCount > 0 && summary.failed === 0;
  const summaryColor = summary?.error ? 'text-red-400' : allPassed ? 'text-green-400' : 'text-yellow-400';
  const summaryText = summary
    ? (summary.error
      ? `Validation error: ${summary.error}`
      : hasSkips
        ? `${realPassed}/${summary.total} passed · ${skippedCount} skipped`
        : `${summary.passed}/${summary.total} tests passed`)
    : null;

  return (
    <div className="mt-3 bg-accent/10 border border-border rounded-lg">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
      >
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Validation Tests</span>
        <div className="flex items-center gap-2">
          {summaryText && <span className={`text-xs font-medium ${summaryColor}`}>{summaryText}</span>}
          <svg className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${collapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {!collapsed && (
        <div className="px-3 pb-3 space-y-0.5">
          {visibleTests.map((test) => (
            <ValidationRow key={test.name} test={test} result={results[test.name]} />
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_DATA_FORM = { preset: 'yahoo', name: 'Yahoo Finance', type: 'both', source: 'free', brokerConnectionId: '', apiKey: '', apiSecret: '' };

function DataProviderModal({ provider, onSave, onClose }) {
  const isEdit = !!provider?.id;
  const [form, setForm] = useState(() => {
    if (provider?.id) {
      const presetInfo = dataPresets.find((p) => p.value === provider.preset);
      const isFree = presetInfo?.free;
      const source = isFree ? 'free' : provider.broker_connection_id ? 'linked' : 'independent';
      return {
        preset: provider.preset,
        name: provider.name,
        type: provider.type,
        source,
        brokerConnectionId: provider.broker_connection_id || '',
        apiKey: provider.api_key || '',
        apiSecret: '',
        role: provider.role || '',
      };
    }
    return { ...EMPTY_DATA_FORM, role: '' };
  });

  const [brokers, setBrokers] = useState([]);
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  const [testMessage, setTestMessage] = useState('');
  const [validationCreds, setValidationCreds] = useState(null);

  const presetInfo = dataPresets.find((p) => p.value === form.preset);
  const isFree = presetInfo?.free ?? false;

  // Load broker connections for "linked" source
  useEffect(() => {
    brokerAPI.getAll().then((res) => setBrokers(res.data ?? [])).catch(() => {});
  }, []);

  function set(patch) {
    setForm((f) => ({ ...f, ...patch }));
    if ('apiKey' in patch || 'apiSecret' in patch) {
      setTestStatus(null);
      setTestMessage('');
      setValidationCreds(null);
    }
  }

  function handlePreset(value) {
    const p = dataPresets.find((d) => d.value === value);
    const newSource = p?.free ? 'free' : brokers.length > 0 ? 'linked' : 'independent';
    set({
      preset: value,
      name: p?.label ?? value,
      type: p?.type ?? 'both',
      source: newSource,
      brokerConnectionId: '',
      apiKey: '',
      apiSecret: '',
    });
    setTestStatus(null);
    setTestMessage('');
    setValidationCreds(null);
  }

  function buildPayload() {
    const payload = {
      preset: form.preset,
      name: form.name,
      type: form.type,
    };
    if (form.source === 'linked' && form.brokerConnectionId) {
      payload.brokerConnectionId = form.brokerConnectionId;
    } else if (form.source === 'independent') {
      payload.apiKey = form.apiKey;
      if (form.apiSecret) payload.apiSecret = form.apiSecret;
    }
    return payload;
  }

  async function handleTest() {
    setTestStatus('testing');
    setTestMessage('');
    try {
      const payload = buildPayload();
      const res = await dataProviderAPI.test(payload);
      if (res.success) {
        const q = res.quote;
        const detail = q ? ` · ${q.symbol} $${parseFloat(q.price).toFixed(2)}` : '';
        setTestStatus('success');
        setTestMessage(res.message + detail);
      } else {
        setTestStatus('error');
        setTestMessage(res.message);
      }
    } catch (err) {
      setTestStatus('error');
      setTestMessage(err.message);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) return setError('Name is required.');
    if (form.source === 'independent' && !form.apiKey.trim()) return setError('API Key is required.');
    if (form.source === 'independent' && !isEdit && !form.apiSecret.trim()) return setError('API Secret is required.');
    if (form.source === 'linked' && !form.brokerConnectionId) return setError('Select a broker connection.');
    onSave(provider?.id, buildPayload(), { role: form.role, oldRole: provider?.role });
  }

  const canTest = isFree || (form.source === 'linked' && form.brokerConnectionId) || (form.source === 'independent' && form.apiKey.trim());

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Data Provider' : 'Add Data Provider'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 px-3 py-2 rounded-lg text-sm">{error}</div>
          )}

          {/* Provider select */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">Provider</label>
            <select
              value={form.preset}
              onChange={(e) => handlePreset(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {dataPresets.map((p) => (
                <option key={p.value} value={p.value}>{p.label}{p.free ? ' (Free)' : ''}</option>
              ))}
            </select>
          </div>

          {/* Data Type */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">Data Type</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'stocks', label: 'Stocks' },
                { value: 'crypto', label: 'Crypto' },
                { value: 'both',   label: 'Both'   },
              ].map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => set({ type: t.value })}
                  className={`py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    form.type === t.value
                      ? 'border-primary bg-primary/20 text-foreground'
                      : 'border-border bg-card text-muted-foreground hover:border-muted-foreground'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Data Source — hidden for free presets */}
          {isFree ? (
            <div className="bg-green-900/20 border border-green-700/40 text-green-300 px-3 py-2.5 rounded-lg text-sm">
              Free provider — no API key required
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Data Source</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => set({ source: 'linked', apiKey: '', apiSecret: '' })}
                    className={`py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                      form.source === 'linked'
                        ? 'border-primary bg-primary/20 text-foreground'
                        : 'border-border bg-card text-muted-foreground hover:border-muted-foreground'
                    }`}
                  >
                    Linked to Broker
                  </button>
                  <button
                    type="button"
                    onClick={() => set({ source: 'independent', brokerConnectionId: '' })}
                    className={`py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                      form.source === 'independent'
                        ? 'border-primary bg-primary/20 text-foreground'
                        : 'border-border bg-card text-muted-foreground hover:border-muted-foreground'
                    }`}
                  >
                    Independent
                  </button>
                </div>
              </div>

              {form.source === 'linked' ? (
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">Broker Connection</label>
                  {brokers.length === 0 ? (
                    <p className="text-xs text-muted-foreground/70">No broker connections found. Add one in Settings &rarr; Brokers first, or choose "Independent".</p>
                  ) : (
                    <select
                      value={form.brokerConnectionId}
                      onChange={(e) => set({ brokerConnectionId: e.target.value })}
                      className="w-full px-3 py-2 bg-background border border-border text-foreground rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">Select a broker...</option>
                      {brokers.map((b) => (
                        <option key={b.id} value={b.id}>{b.name} ({b.preset})</option>
                      ))}
                    </select>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1.5">API Key</label>
                    <input
                      type="text"
                      value={form.apiKey}
                      onChange={(e) => set({ apiKey: e.target.value })}
                      placeholder="Enter API key"
                      className="w-full px-3 py-2 bg-background border border-border text-foreground rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                      API Secret
                      {isEdit && <span className="text-muted-foreground/70 font-normal ml-1">(leave blank to keep existing)</span>}
                    </label>
                    <div className="relative">
                      <input
                        type={showSecret ? 'text' : 'password'}
                        value={form.apiSecret}
                        onChange={(e) => set({ apiSecret: e.target.value })}
                        placeholder={isEdit ? '••••••••••••••••' : 'Enter API secret'}
                        className="w-full px-3 py-2 pr-10 bg-background border border-border text-foreground rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring font-mono text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showSecret ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Test Connection */}
          <div>
            <button
              type="button"
              onClick={handleTest}
              disabled={!canTest || testStatus === 'testing'}
              className={`w-full py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                !canTest
                  ? 'border-border bg-card/50 text-muted-foreground/50 cursor-not-allowed'
                  : testStatus === 'testing'
                  ? 'border-primary/50 bg-primary/10 text-primary cursor-not-allowed'
                  : 'border-primary bg-primary/5 text-primary hover:bg-primary/20 hover:border-primary/80 cursor-pointer'
              }`}
            >
              {testStatus === 'testing' ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner className="h-3.5 w-3.5" />
                  Testing...
                </span>
              ) : 'Test Connection'}
            </button>
            {testStatus === 'success' && (
              <div className="mt-2 flex items-start gap-2 bg-green-900/30 border border-green-700 text-green-300 px-3 py-2 rounded-lg text-xs">
                <span className="mt-0.5 flex-shrink-0">✓</span>
                <span>{testMessage}</span>
              </div>
            )}
            {testStatus === 'error' && (
              <div className="mt-2 flex items-start gap-2 bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded-lg text-xs">
                <span className="mt-0.5 flex-shrink-0">✕</span>
                <span>{testMessage}</span>
              </div>
            )}

            {/* Full Validation */}
            <button
              type="button"
              onClick={() => setValidationCreds({
                preset: form.preset,
                type: form.type,
                ...(form.source === 'linked' && form.brokerConnectionId ? { brokerConnectionId: form.brokerConnectionId } : {}),
                ...(form.source === 'independent' ? { apiKey: form.apiKey, apiSecret: form.apiSecret } : {}),
              })}
              disabled={!canTest}
              className={`mt-2 w-full py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                !canTest
                  ? 'border-border bg-card/50 text-muted-foreground/50 cursor-not-allowed'
                  : 'border-border bg-card text-foreground hover:bg-accent/20 hover:border-muted-foreground cursor-pointer'
              }`}
            >
              Run Full Validation
            </button>
            {validationCreds && <DataValidationPanel credentials={validationCreds} />}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">Role</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: '',          label: 'None'      },
                { value: 'primary',   label: 'Primary'   },
                { value: 'secondary', label: 'Secondary' },
              ].map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => set({ role: r.value })}
                  className={`py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    form.role === r.value
                      ? 'border-primary bg-primary/20 text-foreground'
                      : 'border-border bg-card text-muted-foreground hover:border-muted-foreground'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Primary is used first for price data, secondary as fallback. Only one of each per account.
            </p>
          </div>

          {/* Footer */}
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-1">{isEdit ? 'Update Provider' : 'Add Provider'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DataProviderRow({ provider, onEdit, onToggle, onDelete }) {
  const presetInfo = dataPresets.find((p) => p.value === provider.preset);
  const isFree = presetInfo?.free;
  const key = provider.api_key || '';
  const masked = key ? key.slice(0, 6) + '•'.repeat(Math.max(4, key.length - 6)) : '—';

  const roleBadge = provider.role === 'primary'
    ? <Badge variant="warning">PRIMARY</Badge>
    : provider.role === 'secondary'
    ? <Badge variant="info">SECONDARY</Badge>
    : null;

  return (
    <div className="bg-card border border-border rounded-xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center text-foreground font-bold text-sm flex-shrink-0 select-none">
          {provider.name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="text-sm font-semibold text-foreground">{provider.name}</p>
            <Badge variant={provider.status === 'active' ? 'success' : 'neutral'}>
              {provider.status.toUpperCase()}
            </Badge>
            <Badge variant={TYPE_VARIANT[provider.type] ?? 'neutral'}>
              {TYPE_LABEL[provider.type] ?? provider.type}
            </Badge>
            {roleBadge}
            {isFree && <Badge variant="info">FREE</Badge>}
            {provider.broker_connection_id && <Badge variant="neutral">LINKED</Badge>}
          </div>
          <p className="text-xs text-muted-foreground/70 font-mono">{masked}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
        <ActionBtn onClick={onEdit} className="text-muted-foreground bg-card hover:bg-accent/10">
          Edit
        </ActionBtn>
        <ActionBtn
          onClick={onToggle}
          className={provider.status === 'active'
            ? 'text-yellow-400 bg-yellow-900/30 hover:bg-yellow-900/50'
            : 'text-green-400 bg-green-900/30 hover:bg-green-900/50'
          }
        >
          {provider.status === 'active' ? 'Suspend' : 'Activate'}
        </ActionBtn>
        <ActionBtn onClick={onDelete} className="text-red-400 bg-red-900/30 hover:bg-red-900/50">
          Delete
        </ActionBtn>
      </div>
    </div>
  );
}

function MarketDataSettings() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'add' | <provider row>

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dataProviderAPI.getAll();
      setProviders(res.data ?? []);
    } catch (err) {
      toast.error(`Failed to load data providers: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(id, payload, { role, oldRole } = {}) {
    try {
      let savedId = id;
      if (id) {
        await dataProviderAPI.update(id, payload);
        toast.success('Data provider updated');
      } else {
        const res = await dataProviderAPI.create(payload);
        savedId = res.data?.id;
        toast.success('Data provider added');
      }
      // Set role if changed (works for both add and edit)
      const newRole = role || null;
      const prevRole = oldRole || null;
      if (savedId && newRole !== prevRole) {
        await handleSetRole(savedId, newRole);
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(`Failed to save data provider: ${err.message}`);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this data provider? This cannot be undone.')) return;
    try {
      await dataProviderAPI.remove(id);
      load();
      toast.success('Data provider removed');
    } catch (err) {
      toast.error(`Failed to remove data provider: ${err.message}`);
    }
  }

  async function handleToggle(id) {
    try {
      await dataProviderAPI.toggleStatus(id);
      load();
    } catch (err) {
      toast.error(`Failed to toggle data provider: ${err.message}`);
    }
  }

  async function handleSetRole(id, role) {
    try {
      const res = await dataProviderAPI.setRole(id, role);
      setProviders(res.data ?? []);
      toast.success(role ? `Set as ${role} provider` : 'Role cleared');
    } catch (err) {
      toast.error(`Failed to set role: ${err.message}`);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={() => setModal('add')}>+ Add Provider</Button>
      </div>

      {providers.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <div className="text-5xl mb-4">📡</div>
            <p className="text-muted-foreground mb-1">No data providers configured</p>
            <p className="text-sm text-muted-foreground/70 mb-6">Add a market data provider to get price feeds for your strategies.</p>
            <Button onClick={() => setModal('add')}>+ Add Provider</Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {providers.map((p) => (
            <DataProviderRow
              key={p.id}
              provider={p}
              onEdit={() => setModal(p)}
              onToggle={() => handleToggle(p.id)}
              onDelete={() => handleDelete(p.id)}
            />
          ))}
        </div>
      )}

      {modal && (
        <DataProviderModal
          provider={modal === 'add' ? null : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function SettingsPage() {
  const location = useLocation();
  const section = location.pathname.split('/')[2] || 'general';

  return (
    <PageTransition>
      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        {section === 'general' && <GeneralSettings />}
        {section === 'brokers' && <BrokersSettings />}
        {section === 'market-data' && <MarketDataSettings />}
      </div>
    </PageTransition>
  );
}

export default SettingsPage;
